import { useState } from 'react';

// mui
import { Autocomplete, TextField, Typography, Box, Stack, Grid } from '@mui/material';
import { DotLoading } from '../animation/dot-loading.jsx';

// components
import { impo, Flag, ListboxComponent, PopperComponent } from '../internal.js';

// client-shared
import { str } from '@goldblack/client-utils';

function FilterAutoComplete(props) {
    const { filter } = impo.transience.getValue();
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);

    const isOptionEqualToValue = (option, v) => {
        if (!v) return false;
        return option.toString() === v.toString();
    };

    const showFlag = (index, option) => {
        let code = option.substring(1);
        return Flag(index, code);
    };

    const showTypography = (index, option) => {
        return (
            <Typography key={option + index} sx={{ fontSize: '14px' }}>
                {option}
            </Typography>
        );
    };

    const getOptions = (id) => {
        let option = filter.option.data.find((x) => x._id === id);
        return option ? option.options : null;
    };

    const getOptionLabel = (filterOption, option) => {
        if (!option || option.length === 0) return '';
        return str.formatStringArray(filterOption.optionLabel, Array.isArray(option[0]) ? option[0] : option);
    };

    const renderOption = (props, filterOption, option) => {
        let key = option.join('-');
        let display = str.formatStringArray(filterOption.renderDisplay, option);
        let splits = display.includes('^') ? option : [display];
        return (
            <Box component='li' {...props} key={key}>
                <Stack direction='row' alignItems='center' gap={1}>
                    {splits.map((x, index) => (x.includes('^') ? showFlag(index, x) : showTypography(index, x)))}
                </Stack>
            </Box>
        );
    };

    const openWithOptions = (consent) => {
        // set consent value
        if (consent) {
            props.setConsentValue(true);
            props.setConsentInput(true);
        }

        setOpen(true);

        let options = getOptions(props.filterOption.optionId);
        if (options) {
            setOptions(options);
        } else {
            setLoading(true);
            impo.FilterGetOption(
                props.filterOption.optionId,
                (filterOptions) => {
                    setLoading(false);
                    setOptions(filterOptions.options);
                },
                undefined,
                props.group,
            );
        }
    };

    const onOpen = () => {
        if (props.options === 'inputOptions' && props.filterType.sensitive && !props.consentInput) {
            props.actions?.onShowConsentDialog?.(openWithOptions);
        } else {
            openWithOptions(false);
        }
    };

    const onClose = () => {
        setOpen(false);
        setOptions([]);
    };

    const onChange = (event, value) => {
        props.onChangeValue(props.index, props.filterOption, value);

        //  키보드 닫기
        //if (document.activeElement instanceof HTMLElement) {
        //    document.activeElement.blur();
        //}
    };

    const getOptionDisabled = () => {
        const multiple = props.filterOption.selection > 1;
        const limit = Array.isArray(props.value) && props.value.length >= props.filterOption.selection;
        return multiple && limit;
    };

    return (
        <Grid size={props.filterOption.width}>
            <Autocomplete
                size='small'
                sx={{ width: '100%' }}
                disabled={props.disabled}
                open={open}
                onOpen={onOpen}
                onClose={onClose}
                onChange={onChange}
                loading={loading}
                value={props.value} // controlled
                getOptionLabel={(option) => getOptionLabel(props.filterOption, option)}
                getOptionDisabled={getOptionDisabled}
                isOptionEqualToValue={isOptionEqualToValue}
                limitTags={2}
                multiple={props.filterOption.selection > 1}
                options={options}
                slots={{
                    popper: PopperComponent,
                }}
                slotProps={{
                    listbox: {
                        component: ListboxComponent,
                        style: {
                            height: '40vh',
                        },
                    },
                    paper: {
                        variant: 'outlined',
                    },
                }}
                renderOption={(renProps, option) => renderOption(renProps, props.filterOption, option)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant='standard'
                        slotProps={{
                            htmlInput: {
                                ...params.inputProps, // 반드시 포함 (Autocomplete의 핵심 로직)
                                style: {
                                    fontSize: '16px', // 1. 브라우저는 16px로 인식해서 줌을 안 함
                                    transform: 'scale(0.875)', // 2. 실제 크기를 14px 정도로 축소 (14/16 = 0.875)
                                    transformOrigin: 'left center', // 3. 오른쪽 정렬 유지
                                    textAlign: 'left',
                                    width: '114.2%', // 4. 축소된 만큼 가로 길이를 보정 (100 / 0.875)
                                },
                            },
                            inputLabel: {
                                style: { fontSize: '14px' },
                                sx: {
                                    '&.MuiInputLabel-shrink': {
                                        fontSize: '11px !important', // 2. 입력 후 라벨을 아주 작게 해서
                                        transform: 'translate(0, -2px) scale(1)', // 3. 위로 꽉 붙임
                                    },
                                },
                            },
                            input: {
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? <DotLoading variant='pulse' size={4} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            },
                        }}
                    />
                )}
            />
        </Grid>
    );
}

export { FilterAutoComplete };
